import { useCallback, useEffect, useRef, useState } from 'react';
import type { Message } from '../api/client';
import type { ConversationTransport } from '../api/conversations';
import type { ConnectionStatus } from '../api/hub';
import { useLiveMessages } from './useLiveMessages';

interface UseConversationResult {
  messages: Message[];
  status: ConnectionStatus;
  error: string | null;
  /**
   * Undefined when this transport has no way to post -- a guardian reading their child's thread.
   * A screen must render who does speak here rather than a dead control; there is no disabled
   * composer, because there would be no honest reason to put on it.
   */
  send: ((body: string) => Promise<void>) | undefined;
  /** Exposed so the UI can offer a manual refresh when the socket is down. */
  reconcile: () => Promise<void>;
  /**
   * Records that everything up to the newest loaded message has been shown.
   *
   * Deliberately not called from `reconcile`: the screen decides, because only the screen knows
   * whether the thread is actually in front of somebody. Marking read on fetch would clear the
   * badge every time a background poll succeeded with the phone in a pocket.
   */
  markRead: () => Promise<void>;
}

/**
 * Owns the message list, the cursor, and the hub subscription.
 *
 * The rule this encodes: **Postgres is the source of truth and the cursor fetch is the only way
 * messages enter state.** A hub nudge never carries content — it just triggers the same fetch that
 * runs on first paint and on reconnect. So the live path and the offline path are literally the
 * same code, and there is no second way for a message to arrive that could drift.
 */
export function useConversation(
  transport: ConversationTransport,
  conversationId: string | null | undefined,
): UseConversationResult {
  const live = useLiveMessages();

  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Reported by the one connection rather than owned here. A screen showing "Offline" while the
  // app is plainly connected is worse than no indicator at all.
  const status = live.status;

  const cursorRef = useRef<number>(0);

  // Highest seq already sent to the server. The marker is monotonic server-side too, so this is
  // only about not making the same call on every render.
  const markedRef = useRef<number>(0);
  const reconcilingRef = useRef(false);

  // Held in a ref rather than in the dependency arrays below, and this is load-bearing rather than
  // tidiness: `unitTransport(unitId)` is a fresh object on every render, so depending on it would
  // rebuild `reconcile`, re-run the first-paint effect, set state, and render again -- a fetch loop
  // that only shows up at runtime. The ref means a caller may pass whatever it likes each render.
  const transportRef = useRef(transport);
  transportRef.current = transport;

  const mergeIncoming = useCallback((incoming: Message[]) => {
    if (incoming.length === 0) {
      return;
    }

    setMessages((current) => {
      // Dedupe by id. The optimistic path and the fetched page can describe the same message, and
      // a retry can legitimately return an already-known one.
      const byId = new Map(current.map((m) => [m.id, m]));
      for (const message of incoming) {
        byId.set(message.id, message);
      }
      return [...byId.values()].sort((a, b) => a.seq - b.seq);
    });
  }, []);

  /** Drain everything after the cursor. Loops because a long disconnect can leave many pages. */
  const reconcile = useCallback(async () => {
    if (!conversationId || reconcilingRef.current) {
      return;
    }

    reconcilingRef.current = true;
    try {
      for (;;) {
        const page = await transportRef.current.getMessages(conversationId, cursorRef.current);

        if (page.messages.length === 0) {
          break;
        }

        mergeIncoming(page.messages);
        cursorRef.current = page.messages[page.messages.length - 1].seq;

        if (!page.hasMore) {
          break;
        }
      }
      setError(null);
    } catch {
      setError('Could not load messages. Retrying when the connection returns.');
    } finally {
      reconcilingRef.current = false;
    }
  }, [conversationId, mergeIncoming]);

  // First paint: fetch before any socket exists, so the thread renders whether or not SignalR
  // ever connects.
  useEffect(() => {
    cursorRef.current = 0;
    setMessages([]);
    void reconcile();
  }, [reconcile]);

  // Live updates come from the app's one connection (`LiveMessagesProvider`) rather than a socket
  // per thread. This screen just watches the stream for its own conversation.
  //
  // Deliberately ignores the nudge's seq as content and refetches instead. Trusting the number
  // would make the socket a transport of record, which is exactly what this design rules out --
  // and it is why a missed frame costs nothing.
  useEffect(() => {
    if (!conversationId || live.tick === 0) {
      return;
    }

    // A reconnect bumps the tick with no nudge attached, and that must still resync: every deploy
    // drops every connection, so this is a routine path rather than an edge case.
    if (live.latest === null || live.latest.conversationId === conversationId) {
      void reconcile();
    }
  }, [conversationId, live.tick, live.latest, reconcile]);

  // Polling fallback. SignalR makes it live; this makes it work. Only runs while the socket is
  // down, so a healthy connection costs nothing.
  useEffect(() => {
    if (status === 'connected' || !conversationId) {
      return;
    }

    const timer = setInterval(() => void reconcile(), 10_000);
    return () => clearInterval(timer);
  }, [status, conversationId, reconcile]);

  const canPost = transport.post !== undefined;

  const send = useCallback(
    async (body: string) => {
      const post = transportRef.current.post;
      if (!post || !conversationId) {
        return;
      }

      // Generated client-side and stable across retries, so a resend after a dropped connection
      // returns the original message instead of posting a second one.
      const clientMessageId = crypto.randomUUID();

      try {
        await post(conversationId, body, clientMessageId);
        await reconcile();
        setError(null);
      } catch {
        setError('Message not sent. Check your connection and try again.');
      }
    },
    [conversationId, reconcile],
  );

  const markRead = useCallback(async () => {
    if (!conversationId || messages.length === 0) {
      return;
    }

    const newest = messages[messages.length - 1].seq;
    if (newest <= markedRef.current) {
      return;
    }

    // Optimistic: set before awaiting so a burst of renders makes one call. A failure only means
    // the badge lingers until next time, which is the harmless direction.
    markedRef.current = newest;

    try {
      await transportRef.current.markRead(conversationId, newest);
    } catch {
      markedRef.current = 0;
    }
  }, [conversationId, messages]);

  // A different thread starts from nothing.
  useEffect(() => {
    markedRef.current = 0;
  }, [conversationId]);

  return { messages, status, error, send: canPost ? send : undefined, reconcile, markRead };
}
